import { Controller } from "@hotwired/stimulus"

// The dealer's price entry on a Preisanfrage (Procurement::PortalController#show).
// Keeps the row totals and the sum in step with what is typed (German decimals: "12,50"),
// and saves the draft on its own a moment after the last change. Submitting posts the
// whole form, so it never depends on the last autosave having finished. Without an
// autosave URL (the tenant entering a quote on the dealer's behalf) it only keeps the
// totals in step.
export default class extends Controller {
  static targets = ["row", "price", "notOffered", "lineTotal", "sum", "status"]
  static values = {
    autosaveUrl: String,
    editable: Boolean,
    savedLabel: String,
    failedLabel: String,
    delay: { type: Number, default: 800 },
  }

  connect() {
    this.formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
    this.recalculate()
    if (!this.editableValue) return

    this.onChange = () => {
      this.recalculate()
      this.scheduleSave()
    }
    this.element.addEventListener("input", this.onChange)
    this.element.addEventListener("change", this.onChange)
  }

  disconnect() {
    clearTimeout(this.timer)
    if (this.onChange) {
      this.element.removeEventListener("input", this.onChange)
      this.element.removeEventListener("change", this.onChange)
    }
  }

  recalculate() {
    let sum = 0
    this.rowTargets.forEach((row, index) => {
      const total = this.rowTotal(row, index)
      if (total !== null) sum += total
      this.lineTotalTargets[index].textContent = total === null ? "–" : this.formatter.format(total)
    })
    if (this.hasSumTarget) this.sumTarget.textContent = this.formatter.format(sum)
  }

  rowTotal(row, index) {
    if (this.notOfferedTargets[index]?.checked) return null
    const price = parseDecimal(this.priceTargets[index]?.value)
    const quantity = parseDecimal(row.dataset.quantity)
    if (price === null || quantity === null) return null
    return Math.round(price * quantity * 100) / 100
  }

  scheduleSave() {
    if (!this.autosaveUrlValue) return
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.delayValue)
  }

  async save() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        body: new FormData(this.element),
        headers: { Accept: "application/json", "X-CSRF-Token": token || "" },
        credentials: "same-origin",
      })
      const data = await response.json()
      this.showStatus(
        response.ok
          ? `${this.savedLabelValue} ${data.saved_at}`
          : data.error || this.failedLabelValue,
      )
    } catch {
      this.showStatus(this.failedLabelValue)
    }
  }

  showStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}

// "1.234,5" and "1234.5" both read as 1234.5; empty or unreadable is null.
export function parseDecimal(value) {
  const text = String(value ?? "").trim()
  if (text === "") return null
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text
  const number = Number(normalized)
  return Number.isFinite(number) ? number : null
}
